import * as clipboard from 'clipboard-polyfill'
import {Lang, Language} from 'yougile-lang'
import {logger} from 'yougile-util'
import {Time, UIUtil, Util, UUID} from 'yougile-util'
import {ApiHttpReq, ApiHttpResp, DataObjectId} from 'yougile-model'
import {PageService} from '@extensions/page-service'
import {Backend} from '../../../common/backend'
import {FilePreview} from '../../../common/file-preview'
import {Tracking, TrackingEvent} from '../../../common/tracking'
import {ClientUtilities} from '../../client/client-utilities'
import {globals} from '../../extensions/globals-base'
import {ContextObject} from '../context-object'

type AppSaveAsXlsx = (filename: string, data: string[][]) => void
type AppChooseFile = (callback: (f: File) => void) => void
type AppProgressAddFile = (id: UUID, file: File) => void
type AppProgressUpdate = (id: UUID, progress: number) => void
type AppProgressRemoveFile = (id: UUID) => void

type ClientType = 'mobile' | 'desktop' | 'web'

interface HttpResp {
  status: number
  body: string
  headers: unknown
}

/**
 * @description
 * Collections of common application utilities.
 * @descriptionRu
 * Коллекция утилит приложения.
 */
export class App extends ContextObject {
  private static saveAsXlsx: AppSaveAsXlsx
  private static chooseFile: AppChooseFile
  private static progressAddFile: AppProgressAddFile
  private static progressUpdate: AppProgressUpdate
  private static progressRemoveFile: AppProgressRemoveFile

  /**
   * @code
   * App.yougileUserId;
   * @description
   * Returns string with id of System YouGile user
   * @descriptionRu
   * Возвращает строку, в которой ID системного пользователя YouGile.
   */
  public static yougileUserId = '0000bdc7-0363-4d30-b5d3-5e0fc72f811f'

  public static override registered: Map<DataObjectId, Map<string, App>> =
    new Map()

  public static init(
    saveAsXlsx: AppSaveAsXlsx,
    chooseFile: AppChooseFile,
    progressAddFile: AppProgressAddFile,
    progressUpdate: AppProgressUpdate,
    progressRemoveFile: AppProgressRemoveFile
  ): void {
    App.saveAsXlsx = saveAsXlsx
    App.chooseFile = chooseFile
    App.progressAddFile = progressAddFile
    App.progressUpdate = progressUpdate
    App.progressRemoveFile = progressRemoveFile
  }

  /**
   * @code
   * App.clientType();
   * @description
   * Returns the string with a type of the client,
   * available types are: 'web', 'mobile', 'desktop'.
   * @descriptionRu
   * Возвращает строку с типом клиента.
   * Доступные типы: 'web', 'mobile', 'desktop'.
   */
  public clientType(): ClientType {
    return !Util.isDesktop() ? 'web' : 'desktop'
  }

  /**
   * @code
   * App.time();
   * @description
   * Returns unix timestamp of server time.
   * Useful for teams with different
   *  timezones.
   * @descriptionRu
   * Возврашает unix timestamp серверного времени.
   * (unix timestamp — количество миллисекунд, прошедшее с 00:00 1 янв 1970
   * по Гринвичу)
   * @returns {number}
   */
  public time(): number {
    return Time.now()
  }

  /**
   * @code
   * App.lang();
   * @description
   * Returns UI language: 'en', 'ru'.
   * @descriptionRu
   * Возвращает текущий язык пользовательского интерфейса: 'en', 'ru'.
   */
  public lang(): Language {
    return Lang.getLang()
  }

  public get theme(): 'light' | 'dark' {
    return globals.theme
  }

  public get desktopVersion(): string | undefined {
    return globals.desktopVersion
  }

  public get host(): string | undefined {
    return globals.webPlatformUrl
  }

  public get isStandalone(): boolean {
    return !!globals.standalone
  }

  public get analytics(): {
    send: (event: TrackingEvent, eventProps?: Record<string, unknown>) => void
    sendExtensionUsage: () => void
  } {
    const sendExtensionUsage = (): void => {
      if (!this.context.extensionId) {
        logger.warn('App.analytics.usage called without extensionId')
      }
      const event = `ExtensionUsage:${Util.kebabToPascal(
        this.context.extensionId
      )}`
      Tracking.sendThrottled(
        event as TrackingEvent,
        {
          userId: globals.currentUserId,
          companyId: this.context.box.companyId
        },
        60 * 60 * 1000 // 1 hour throttle
      )
    }
    return {
      send: (event: TrackingEvent, eventProps?: Record<string, unknown>) => {
        Tracking.send(event, eventProps)
        if (this.context.extensionId) {
          sendExtensionUsage()
        }
      },
      /**
       * Sends an event that extension is used. This type of event can be sent only once per minute for each extension.
       */
      sendExtensionUsage
    }
  }

  /**
   * @code
   * App.formatDateTime(App.time());
   * @description
   * Converts server timestamp (see App.time) to
   * a human-readable date-time string.
   * @descriptionRu
   * Конвертирует timestamp с сервера (см. App.time)
   * в строку с датой и временем. Может применяться как с маской так и без
   * @example
   * var timestamp = App.time();
   * var result = App.formatDateTime(timestamp, 'DD.MM.YYYY hh:mm');
   * Notifier.success('Now is ' + result);
   */
  public formatDateTime(timestamp: number, options?: any): string {
    if (!options) {
      return Time.formatDateTime(timestamp)
    }
    if (typeof options === 'string') {
      return Time.formatCustom(timestamp, options)
    }
    if (typeof options === 'object') {
      const {alwaysWithYear, skipCurrentDate, round, fullMonth, comma} = options
      return Time.formatDateTime(
        timestamp,
        alwaysWithYear,
        skipCurrentDate,
        round,
        fullMonth,
        comma
      )
    }

    return Time.formatDateTime(timestamp)
  }

  /**
   * @code
   * App.saveCsv('...', []);
   * @example
   * // creating rows
   * var headerRow = ['Num', 'Name', 'Price', 'Amount'];
   * var row1 = [1, 'Brick', 1.45, 1200];
   * var row2 = [2, 'Stone', 0.57, 100];
   * var row3 = [3, 'Wood', 0.99, 1450];
   * var row4 = [4, 'Iron', 1.22, 800];
   *
   * // array of rows is a table
   * var data = [headerRow, row1, row2, row3, row4];
   * // download this table in .csv tsd format
   * App.saveCsv('my-data.csv', data);
   * @description
   * Save array to .csv file and download it.
   * @descriptionRu
   * Сохраняет массив в .csv-файл и скачивает его.
   */
  public saveCsv(filename: string, data: string[][], delim: string): void {
    if (!filename.endsWith('.csv')) {
      filename += '.csv'
    }
    if (typeof delim === 'undefined') {
      delim = ','
    }
    UIUtil.downloadAsCsv(filename, data, '', '', delim)
  }

  /**
   * @code
   * App.saveXlsx('...', []);
   * @example
   * // creating rows
   * var headerRow = ['Num', 'Name', 'Price', 'Amount'];
   * var row1 = [1, 'Brick', 1.45, 1200];
   * var row2 = [2, 'Stone', 0.57, 100];
   * var row3 = [3, 'Wood', 0.99, 1450];
   * var row4 = [4, 'Iron', 1.22, 800];
   *
   * // array of rows is a table
   * var data = [headerRow, row1, row2, row3, row4];
   * // download this table in .csv tsd format
   * App.saveXlsx('my-data.xlsx', data);
   * @description
   * Save array to .xlsx file and download it.
   * @descriptionRu
   * Сохраняет массив в .xlsx-файл и скачивает его.
   */
  public saveXlsx(filename: string, data: string[][]): void {
    App.saveAsXlsx(filename, data)
    // saveAsXlsx(filename, data);
  }

  /**
   * @code
   * App.chooseFile(function (file) {
   *
   * });
   * @description
   * Asks user to choose a local file.
   * After user chooses a file, callback function is called and this
   * file is passed to it.
   * @descriptionRu
   * Предлагает пользователю выбрать файл с компьютера.
   * После выбора файла вызывается callback, в который передаётся
   * этот файл.
   * @example
   * App.chooseFile(function (file) {
   *   Notifier.success('You’ve chosen file: ' + file.name);
   * });
   */
  public chooseFile(callback: (f: File) => void): void {
    App.chooseFile(callback)
    // FileChooser.chooseFile(undefined, callback);
  }

  /**
   * @code
   * App.chooseAndUpload(function (url) {
   *
   * });
   * @description
   * Asks user to choose a local file, then uploads it to
   * YouGile server and passes the url of that uploaded file
   * to “callback”.
   * @descriptionRu
   * Предлагает пользователю выбрать файл, затем заливает
   * этот файл на сервер и вызывает callback, куда передаёт
   * url адрес залитого файла.
   * @example
   * App.chooseAndUpload(function (url) {
   *   Notifier.success('File is uploaded to ' + url);
   * });
   */
  public chooseAndUpload(callback: (url: string) => void): void {
    this.chooseFile((f: File) => this.uploadFile(f, callback))
  }

  /**
   * @code
   * App.uploadFile(file, function (url) {
   *
   * });
   * @description
   * Uploads the specified file to YouGile server and passes the url
   * of the uploaded file to the callback.
   * @descriptionRu
   * Загружает файл на сервер YouGile и вызывает callback, куда передаёт
   * адрес загруженного файла.
   * @example
   * // suppose we have a file already from Api.chooseFile
   * // function or from <input type="file" ..../>
   * Api.upload(file, function (url) {
   *   Notifier.success('File uploaded to url ' + url);
   * });
   */
  public uploadFile(file: File, callback: (url: string) => void): void {
    const id = Util.UUID()
    App.progressAddFile(id, file)
    // FileUploadProgress.addFile(id, file);
    ClientUtilities.saveFile(
      {file},
      (url: string) => {
        App.progressRemoveFile(id)
        // FileUploadProgress.removeFile(id);
        if (!url) {
          return
        }
        callback(url)
      },
      (progress: number) => {
        App.progressUpdate(id, progress)
        // FileUploadProgress.updateProgress(id, progress);
      }
    )
  }

  /**
   * @code
   * App.getPreviewUrl('... url');
   * @description
   * If image file was uploaded to YouGile server
   * (see App.chooseAndUpload) then small preview was
   * automatically generated and information about it is stored
   * in get-parameters of the uploaded image url. To get the url of
   * this preview you can use this function.
   * @descriptionRu
   * Когда файл с изображением заливается на сервер YouGile
   * (см. App.chooseAndUpload), автоматически создаётся небольшое превью,
   * информация о котором сохраняется в get-параметрах url-адреса
   * изображения. Эта функция (App.getPreviewUrl) позволяет получить
   * url превью изображения.
   * @example
   * App.chooseAndUpload(function (url) {
   *   // here we have an url of the uploaded file
   *   // let’s get a preview
   *   var preview = App.getPreviewUrl(url);
   *   // now we can add it as an image url somewhere
   *   // let’s add to the top of the chat list
   *   UI.components.chat.add(UI.image(preview));
   * });
   */
  public getPreviewUrl(url: string): string {
    return FilePreview.previewFromUrl(url)
  }

  /**
   * @code
   * App.httpGet('...', function (res) {
   *
   * });
   * @description
   * Allows to make HTTP GET requests.
   * @descriptionRu
   * Позволяет делать HTTP GET запросы.
   * @example
   * App.httpGet('http://my-site.com/data?key=123', function (res) {
   *   var status = res.status; // status code
   *   if (status === 200) {
   *     var headers = res.headers; // response headers
   *     var body = res.body; // response body
   *   }
   *  });
   */
  public httpGet(url: string, cb?: (res: HttpResp) => void): Promise<HttpResp> {
    const userId = Backend.loadId()
    const userKey = Backend.loadKey()
    if (globals.currentUserId !== userId || !userKey) {
      cb?.({
        status: 401,
        headers: [],
        body: 'Not authorized'
      })
      return Promise.resolve({status: 401, headers: [], body: 'Not authorized'})
    }
    const uriEnc = encodeURIComponent(url)
    const path = `/data/http/get?userId=${userId}&key=${userKey}&uri=${uriEnc}&companyId=${this.context.box.companyId}`
    return Backend.getJSON<ApiHttpResp>(path)
      .then(res => {
        const {status, headers, body} = res
        cb?.({status, headers, body})
        return {status, headers, body}
      })
      .catch(err => {
        const resp: HttpResp = {status: 400, headers: [], body: 'Error: ' + err}
        cb?.(resp)
        return resp
      })
  }

  /**
   * @code
   * App.httpPost('...', {});
   * @description
   * Allows to make HTTP POST requests.
   * @descriptionRu
   * Позволяет делать HTTP POST запросы.
   * @example
   * var formData = {
   *   key1: 123,
   *   param2: 'test'
   * };
   * App.httpPost('http://my-site.com', formData).then(function (res) {
   *   var status = res.status; // status code
   *   if (status === 200) {
   *     var headers = res.headers; // response headers
   *     var body = res.body; // response body
   *   }
   *  });
   *
   * // with custom headers and form-encoded data
   * var data = {
   *  some: 123
   * };
   * var requestHeaders = {
   *  'Content-Type': 'application/x-www-form-urlencoded',
   *  'api-key': 'abc'
   * };
   * App.httpPost('http://a.com', data, requestHeaders).then(function (res) {
   *   var status = res.status; // status code
   *   if (status === 200) {
   *     var headers = res.headers; // response headers
   *     var body = res.body; // response body
   *   }
   *  });
   */
  public httpPost(
    url: string,
    params: unknown,
    headers?: {[name: string]: string},
    cb?: (res: HttpResp) => void
  ): Promise<HttpResp> {
    // backward compatibility
    if (typeof headers === 'function') {
      cb = headers
      headers = undefined
    }
    //

    const userId = Backend.loadId()
    const key = Backend.loadKey()
    if (globals.currentUserId !== userId || !key) {
      cb?.({status: 401, headers: [], body: 'Not authorized'})
      return Promise.resolve({
        status: 401,
        headers: [],
        body: 'Not authorized'
      })
    }
    const uri = encodeURIComponent(url)
    const dt: ApiHttpReq = {
      params,
      userId,
      key,
      uri,
      companyId: this.context.box.companyId
    }
    return Backend.postJSON<ApiHttpResp>('/data/http/post', dt)
      .then(res => {
        const {status, body, headers: headers2} = res
        cb?.({status, headers: headers2, body})
        return {status, headers: headers2, body}
      })
      .catch(error => {
        const resp: HttpResp = {
          status: 400,
          headers: [],
          body: 'Error: ' + error
        }
        cb?.(resp)
        return resp
      })
  }

  /**
   * @code
   * App.copyToClipboard('');
   * @description
   * Copies text to clipboard.
   * @descriptionRu
   * Копирует текст в буфер обмена.
   * @example
   * App.copyToClipboard('Some text');
   */
  public copyToClipboard(text: string): Promise<void> {
    return clipboard.writeText(text + '')
  }

  /**
   * @code
   * App.allowUsersToInvite = false;
   * @description
   * If false, then users (not admins) can’t add another users to projects or
   * invite other users to the company.
   * @descriptionRu
   * Если false, то пользователи (не админы) не могут добавлять кого-либо
   * в проекты или приглашать новых пользователей в компанию.
   * @example
   * App.allowUsersToInvite = false;
   */
  public get allowUsersToInvite(): boolean {
    return globals.allowUsersToInvite
  }

  public set allowUsersToInvite(value: boolean) {
    globals.allowUsersToInvite = value
  }

  /**
   * @code
   * App.setPath('/my/path');
   * @description
   * Sets the path of the current page.
   * @descriptionRu
   * Устанавливает путь текущей страницы.
   * @param path
   */
  public setPath(path: string): void {
    PageService.setPath(path)
  }

  /**
   * @todo implement
   */
  public onDisconnect: () => void

  /**
   * @todo implement
   */
  public onReconnect: () => void

  /**
   * @todo implement
   */
  public onRemoteUpdate: () => void

  /**
   * @todo implement
   */
  public onLocalUpdate: () => void
}
